/**
 * ÉCRAN LIVE — Jeux Martiaux : points par école en direct (écran du dojo, plein écran, rafraîchi toutes les 15 s).
 * Public : GET /api/mini-competitions/public/{slug}/live
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL;
const LEVEL_COLOR = { "Performance remarquable": "text-amber-300", "Belle progression": "text-sky-300", "Participation engagée": "text-emerald-300" };

export default function LiveScoreboardPublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/api/mini-competitions/public/${slug}/live`)
      .then((r) => { if (alive) { setData(r.data); setErr(null); } })
      .catch(() => alive && setErr("Événement introuvable"));
    load();
    const t = setInterval(() => { load(); setTick((x) => x + 1); }, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [slug]);

  if (err) return <div className="min-h-screen bg-zinc-950 text-zinc-300 flex items-center justify-center text-2xl" data-testid="live-error">{err}</div>;
  if (!data) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center text-xl">Chargement…</div>;
  const { event, schools, matches, recent_matches, by_discipline } = data;
  const max = Math.max(1, ...schools.map((s) => s.points));
  const discLabel = (d) => (event.discipline_labels || {})[d] || d;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-10 py-8 font-sans" data-testid="live-scoreboard">
      <header className="flex items-end justify-between gap-6 border-b border-amber-500/30 pb-5 mb-8">
        <div>
          <div className="text-amber-400 text-xs tracking-[0.4em] uppercase font-bold">Réseau Kaizen · Tableau des points en direct</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mt-1" data-testid="live-event-name">{event.name}</h1>
          <div className="text-zinc-400 mt-1 text-lg">{event.date} · {event.location}</div>
        </div>
        <div className="flex items-center gap-6 shrink-0">
          <div className="bg-white rounded-2xl p-3 shadow-lg shadow-amber-500/10" data-testid="live-qr" title="Suivez les points sur votre téléphone">
            <QRCodeSVG value={typeof window !== "undefined" ? window.location.href : ""} size={112} level="M" />
            <div className="text-[10px] text-zinc-700 text-center mt-1 font-bold uppercase tracking-wider">📱 Suivez les points</div>
          </div>
        <div className="text-right">
          <div className="text-6xl font-black text-amber-400 tabular-nums" data-testid="live-matches">{matches.done}<span className="text-zinc-600 text-3xl">/{matches.total}</span></div>
          <div className="text-zinc-500 text-sm uppercase tracking-widest">combats complétés</div>
          <div className="text-zinc-600 text-xs mt-1 flex items-center justify-end gap-1"><span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" /> live · maj auto {tick > 0 ? `#${tick}` : ""}</div>
        </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-5 gap-8">
        <section className="lg:col-span-3">
          <h2 className="text-base md:text-lg uppercase tracking-[0.3em] text-zinc-400 font-bold mb-4">Points cumulés par école</h2>
          {schools.length === 0 ? (
            <div className="text-zinc-500 text-2xl py-16 text-center border border-dashed border-zinc-800 rounded-2xl" data-testid="live-empty">Les premiers combats arrivent… 🥋</div>
          ) : (
            <div className="space-y-4" data-testid="live-schools">
              {schools.map((s, i) => (
                <div key={s.school_name} className="relative rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 overflow-hidden" data-testid={`live-school-${i}`}
                  style={{ animation: `fadeUp .5s ease ${i * 80}ms both` }}>
                  <div className="absolute inset-y-0 left-0 bg-amber-500/10" style={{ width: `${(s.points / max) * 100}%`, transition: "width .8s ease" }} />
                  <div className="relative flex items-center justify-between gap-4">
                    <div>
                      <div className="text-2xl md:text-3xl font-black">{s.school_name}</div>
                      <div className="text-zinc-400 text-sm">{s.athletes} athlète{s.athletes > 1 ? "s" : ""}{s.remarquables ? ` · ${s.remarquables} performance${s.remarquables > 1 ? "s" : ""} remarquable${s.remarquables > 1 ? "s" : ""}` : ""}</div>
                    </div>
                    <div className="text-5xl font-black text-amber-400 tabular-nums">{s.points}<span className="text-lg text-zinc-500 ml-1">pts</span></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="text-base md:text-lg uppercase tracking-[0.3em] text-zinc-400 font-bold mb-3">Derniers combats</h2>
            <ul className="space-y-2" data-testid="live-recent">
              {recent_matches.length === 0 && <li className="text-zinc-600 text-sm">Aucun combat terminé.</li>}
              {recent_matches.map((m, i) => (
                <li key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.category_label}{m.method ? ` · ${m.method}` : ""}</div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className={m.winner === "a" ? "text-emerald-300 font-bold" : "text-zinc-300"}>{m.a}</span>
                    <span className="text-zinc-600 text-xs">{m.winner === "draw" ? "nul" : "vs"}</span>
                    <span className={m.winner === "b" ? "text-emerald-300 font-bold" : "text-zinc-300"}>{m.b}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {Object.entries(by_discipline).map(([d, rows]) => (
            <div key={d}>
              <h3 className="text-sm uppercase tracking-[0.3em] text-amber-400/80 font-bold mb-2">{discLabel(d)}</h3>
              <ul className="divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 overflow-hidden">
                {rows.map((r, i) => (
                  <li key={i} className="flex items-center justify-between px-4 py-2 text-sm bg-zinc-900/30">
                    <div><span className="font-semibold">{r.athlete_full_name}</span> <span className="text-zinc-500">· {r.school_name}</span>
                      <div className={`text-[10px] ${LEVEL_COLOR[r.level_label] || "text-zinc-500"}`}>{r.level_label} · {r.victoires}V/{r.combats}C</div></div>
                    <div className="font-black text-amber-300 tabular-nums">{r.total_points}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </aside>
      </div>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
